import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Building2, Users, Shield, Key, ArrowLeft, Edit, Save,
  UserPlus, Mail, Trash2, CheckCircle, Crown, Settings,
  Search, AlertCircle, DollarSign, Package, UserCheck, UserX, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  getTenant,
  updateTenant,
  listTenantMembers,
  inviteMember,
  updateMember,
  removeMember,
  getLicenseTiers,
  getModules,
  updateTenantModules,
  getMyTenants,
  searchPlatformUsers,
  updateTenantPricing,
  LICENSE_TIER_COLORS,
  ROLE_COLORS,
  MODULE_ICONS
} from '@/services/admin';

const TenantDetailPage = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  
  const [tenant, setTenant] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [licenseTiers, setLicenseTiers] = useState([]);
  const [modules, setModules] = useState([]);
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  
  // Invite dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'viewer' });
  const [inviting, setInviting] = useState(false);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState(null); // null=not searched, []|user=result
  const [inviteSearching, setInviteSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const searchDebounceRef = useRef(null);

  // Pricing state (super admin)
  const [pricingData, setPricingData] = useState({ custom_monthly_price: '', pricing_notes: '' });
  const [savingPricing, setSavingPricing] = useState(false);
  const TIER_DEFAULT_PRICES = { free: 0, starter: 149, professional: 249, enterprise: 799 };

  useEffect(() => {
    loadData();
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const myTenants = await getMyTenants();
      setIsSuperAdmin(myTenants.is_super_admin);
      
      const [tenantData, membersData, tiersData, modulesData] = await Promise.all([
        getTenant(tenantId),
        listTenantMembers(tenantId),
        getLicenseTiers(),
        getModules()
      ]);
      
      setTenant(tenantData);
      setMembers(membersData.items || []);
      setLicenseTiers(tiersData.tiers || []);
      setModules(modulesData.modules || []);
      setEditData(tenantData);
      setPricingData({
        custom_monthly_price: tenantData.custom_monthly_price ?? '',
        pricing_notes: tenantData.pricing_notes ?? ''
      });
    } catch (err) {
      console.error('Failed to load tenant:', err);
      toast.error('Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      
      if (editData.name !== tenant.name) updates.name = editData.name;
      if (editData.domains?.join(',') !== tenant.domains?.join(',')) {
        updates.domains = editData.domains;
      }
      if (editData.auditor_domains?.join(',') !== tenant.auditor_domains?.join(',')) {
        updates.auditor_domains = editData.auditor_domains;
      }
      
      // Super admin only fields
      if (isSuperAdmin) {
        if (editData.license_tier !== tenant.license_tier) {
          updates.license_tier = editData.license_tier;
        }
        if (editData.max_users !== tenant.max_users) {
          updates.max_users = editData.max_users;
        }
        if (editData.status !== tenant.status) {
          updates.status = editData.status;
        }
      }
      
      if (Object.keys(updates).length > 0) {
        await updateTenant(tenantId, updates);
        toast.success('Tenant updated successfully');
        loadData();
      }
      
      setIsEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tenant');
    } finally {
      setSaving(false);
    }
  };

  const handleModuleToggle = async (moduleId, enabled) => {
    if (!isSuperAdmin) return;
    
    try {
      const currentModules = tenant.enabled_modules || [];
      let newModules;
      
      if (enabled) {
        newModules = [...currentModules, moduleId];
      } else {
        newModules = currentModules.filter(m => m !== moduleId);
      }
      
      await updateTenantModules(tenantId, newModules);
      toast.success('Modules updated');
      loadData();
    } catch (err) {
      toast.error('Failed to update modules');
    }
  };

  // Debounced user search for invite dialog
  const handleInviteEmailChange = useCallback((val) => {
    setInviteSearch(val);
    setInviteData(prev => ({ ...prev, email: val }));
    setFoundUser(null);
    setInviteSearchResults(null);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!val || val.length < 3) return;
    searchDebounceRef.current = setTimeout(async () => {
      setInviteSearching(true);
      try {
        const data = await searchPlatformUsers(val);
        const users = data.users || [];
        setInviteSearchResults(users);
        const exact = users.find(u => u.email.toLowerCase() === val.toLowerCase());
        setFoundUser(exact || null);
      } catch {
        setInviteSearchResults([]);
      } finally {
        setInviteSearching(false);
      }
    }, 500);
  }, []);

  const handleInvite = async () => {
    if (!inviteData.email) {
      toast.error('Please enter an email address');
      return;
    }
    setInviting(true);
    try {
      const result = await inviteMember(tenantId, inviteData);
      if (result.user_created) {
        toast.success(`New account created and ${inviteData.email} added to ${tenant.name}. Credentials sent via email.`);
      } else {
        toast.success(`${inviteData.email} has been added to ${tenant.name}.`);
      }
      setShowInviteDialog(false);
      setInviteData({ email: '', role: 'viewer' });
      setInviteSearch('');
      setFoundUser(null);
      setInviteSearchResults(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setInviting(false);
    }
  };

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const payload = {};
      if (pricingData.custom_monthly_price !== '' && pricingData.custom_monthly_price !== null) {
        payload.custom_monthly_price = parseFloat(pricingData.custom_monthly_price) || null;
      }
      if (pricingData.pricing_notes !== undefined) payload.pricing_notes = pricingData.pricing_notes;
      await updateTenantPricing(tenantId, payload);
      toast.success('Pricing updated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const handleRemoveMember = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from this tenant?`)) return;
    
    try {
      await removeMember(tenantId, userId);
      toast.success('Member removed');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateMember(tenantId, userId, { role: newRole });
      toast.success('Role updated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Tenant not found</p>
        <Button onClick={() => navigate('/admin')} className="mt-4">
          Back to Admin
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Building2 className="w-7 h-7 text-indigo-600" />
              {tenant.name}
            </h1>
            <p className="text-gray-500 mt-1">
              <code className="bg-gray-100 px-2 py-0.5 rounded">{tenant.slug}</code>
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="members">Members ({members.length})</TabsTrigger>
          <TabsTrigger value="modules">Modules</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="settings">Settings</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="pricing">Pricing</TabsTrigger>}
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Tenant Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Status</Label>
                    <p className={`mt-1 inline-block px-2 py-1 rounded text-sm ${
                      tenant.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100'
                    }`}>
                      {tenant.status || 'unknown'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-500">License Tier</Label>
                    <p className={`mt-1 inline-block px-2 py-1 rounded text-sm border ${
                      LICENSE_TIER_COLORS[tenant.license_tier] || 'bg-gray-100'
                    }`}>
                      {tenant.license_tier || 'free'}
                    </p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-gray-500">Allowed Domains</Label>
                  <p className="mt-1">
                    {tenant.domains?.length > 0 
                      ? tenant.domains.join(', ') 
                      : <span className="text-gray-400">No domain restrictions</span>
                    }
                  </p>
                </div>
                
                <div>
                  <Label className="text-gray-500">Auditor Domains</Label>
                  <p className="mt-1">
                    {tenant.auditor_domains?.length > 0 
                      ? tenant.auditor_domains.join(', ') 
                      : <span className="text-gray-400">Any domain allowed for auditors</span>
                    }
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Members</Label>
                    <p className="mt-1 text-2xl font-bold">
                      {tenant.member_count || 0} / {tenant.max_users || '?'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Pending Invites</Label>
                    <p className="mt-1 text-2xl font-bold">{tenant.pending_invitations || 0}</p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-gray-500">Created</Label>
                  <p className="mt-1">{tenant.created_at ? new Date(tenant.created_at).toLocaleString() : 'N/A'}</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Enabled Modules</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {modules.map(module => {
                    const isEnabled = (tenant.enabled_modules || []).includes(module.id);
                    return (
                      <div 
                        key={module.id}
                        className={`p-3 rounded-lg border ${
                          isEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{MODULE_ICONS[module.id] || '📦'}</span>
                          <div>
                            <p className="font-medium">{module.name}</p>
                            <p className="text-xs text-gray-500">{module.description}</p>
                          </div>
                          {isEnabled && (
                            <CheckCircle className="w-4 h-4 text-green-500 ml-auto" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Team Members</CardTitle>
                <CardDescription>
                  {members.length} members in this tenant
                </CardDescription>
              </div>
              <Button onClick={() => setShowInviteDialog(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            </CardHeader>
            <CardContent>
              {members.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No members yet. Invite someone to get started.
                </div>
              ) : (
                <div className="space-y-3">
                  {members.map(member => (
                    <div 
                      key={member.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-600 font-medium">
                            {(member.full_name || member.email || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{member.full_name || member.email}</p>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <Select
                          value={member.role}
                          onValueChange={(v) => handleRoleChange(member.user_id, v)}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="team_member">Team Member</SelectItem>
                            <SelectItem value="auditor">Auditor</SelectItem>
                            <SelectItem value="viewer">Viewer</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveMember(member.user_id, member.email)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Modules Tab */}
        <TabsContent value="modules">
          <Card>
            <CardHeader>
              <CardTitle>Module Configuration</CardTitle>
              <CardDescription>
                Enable or disable platform modules for this tenant
                {!isSuperAdmin && ' (Super admin only)'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {modules.map(module => {
                  const isEnabled = (tenant.enabled_modules || []).includes(module.id);
                  return (
                    <div 
                      key={module.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{MODULE_ICONS[module.id] || '📦'}</span>
                        <div>
                          <p className="font-medium">{module.name}</p>
                          <p className="text-sm text-gray-500">{module.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={isEnabled}
                          onCheckedChange={(checked) => handleModuleToggle(module.id, checked)}
                          disabled={!isSuperAdmin}
                        />
                        <span className={isEnabled ? 'text-green-600' : 'text-gray-400'}>
                          {isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab (Super Admin) */}
        {isSuperAdmin && (
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Tenant Settings</CardTitle>
                <CardDescription>
                  Manage license, limits, and administrative settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>License Tier</Label>
                    <Select 
                      value={editData.license_tier} 
                      onValueChange={(v) => setEditData(prev => ({ ...prev, license_tier: v }))}
                      disabled={!isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {licenseTiers.map(tier => {
                          const tierKey = tier.tier || tier.id || '';
                          return (
                            <SelectItem key={tierKey} value={tierKey}>
                              {tierKey.charAt(0).toUpperCase() + tierKey.slice(1)}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Max Users</Label>
                    <Input
                      type="number"
                      min={1}
                      value={editData.max_users || 5}
                      onChange={(e) => setEditData(prev => ({ 
                        ...prev, 
                        max_users: parseInt(e.target.value) || 5 
                      }))}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={editData.status} 
                    onValueChange={(v) => setEditData(prev => ({ ...prev, status: v }))}
                    disabled={!isEditing}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="deactivated">Deactivated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Allowed Domains (comma-separated)</Label>
                  <Input
                    value={(editData.domains || []).join(', ')}
                    onChange={(e) => setEditData(prev => ({ 
                      ...prev, 
                      domains: e.target.value.split(',').map(d => d.trim()).filter(Boolean)
                    }))}
                    disabled={!isEditing}
                    placeholder="example.com, example.co.uk"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Auditor Domains (comma-separated)</Label>
                  <Input
                    value={(editData.auditor_domains || []).join(', ')}
                    onChange={(e) => setEditData(prev => ({ 
                      ...prev, 
                      auditor_domains: e.target.value.split(',').map(d => d.trim()).filter(Boolean)
                    }))}
                    disabled={!isEditing}
                    placeholder="auditfirm.com"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Pricing Tab (Super Admin only) */}
        {isSuperAdmin && (
          <TabsContent value="pricing">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  Pricing Configuration
                </CardTitle>
                <CardDescription>Set custom pricing and module access for this tenant.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Default tier price */}
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <p className="text-sm text-muted-foreground mb-1">Default price for <strong className="text-foreground capitalize">{tenant.license_tier || 'free'}</strong> tier</p>
                  <p className="text-2xl font-bold text-slate-800">
                    ${TIER_DEFAULT_PRICES[tenant.license_tier || 'free'] ?? 0}
                    <span className="text-sm font-normal text-muted-foreground">/month</span>
                  </p>
                  {tenant.custom_monthly_price != null && (
                    <p className="text-sm text-emerald-600 mt-1">
                      Custom override: <strong>${tenant.custom_monthly_price}/month</strong>
                    </p>
                  )}
                </div>

                {/* Custom price override */}
                <div className="space-y-2">
                  <Label>Custom Monthly Price (USD)</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={`Default: ${TIER_DEFAULT_PRICES[tenant.license_tier] ?? 0}`}
                      value={pricingData.custom_monthly_price}
                      onChange={(e) => setPricingData(prev => ({ ...prev, custom_monthly_price: e.target.value }))}
                      className="w-40"
                      data-testid="custom-price-input"
                    />
                    <span className="text-sm text-muted-foreground">/month</span>
                    {pricingData.custom_monthly_price !== '' && (
                      <Button variant="ghost" size="sm" onClick={() => setPricingData(prev => ({ ...prev, custom_monthly_price: '' }))}>
                        Clear
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Leave blank to use default tier price.</p>
                </div>

                {/* License tier */}
                <div className="space-y-2">
                  <Label>License Tier</Label>
                  <Select value={editData.license_tier || tenant.license_tier || 'free'}
                    onValueChange={(v) => setEditData(prev => ({ ...prev, license_tier: v }))}>
                    <SelectTrigger className="w-48" data-testid="license-tier-select">
                      <SelectValue placeholder="Select tier..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[
                        { id: 'free', name: 'Free — $0' },
                        { id: 'starter', name: 'Starter — $149' },
                        { id: 'professional', name: 'Professional — $249' },
                        { id: 'enterprise', name: 'Enterprise — $799' },
                      ].map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Pricing notes */}
                <div className="space-y-2">
                  <Label>Pricing Notes</Label>
                  <Input
                    placeholder="e.g. Annual contract, 20% discount applied"
                    value={pricingData.pricing_notes}
                    onChange={(e) => setPricingData(prev => ({ ...prev, pricing_notes: e.target.value }))}
                    data-testid="pricing-notes-input"
                  />
                </div>

                {/* Save */}
                <Button onClick={handleSavePricing} disabled={savingPricing} data-testid="save-pricing-btn">
                  {savingPricing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Pricing</>}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Invite Dialog - Enhanced with user search */}
      <Dialog open={showInviteDialog} onOpenChange={(open) => {
        setShowInviteDialog(open);
        if (!open) { setInviteSearch(''); setFoundUser(null); setInviteSearchResults(null); setInviteData({ email: '', role: 'viewer' }); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Member to {tenant.name}</DialogTitle>
            <DialogDescription>
              Search by email. If the user doesn't exist, a new account will be created and credentials sent.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Email search */}
            <div className="space-y-2">
              <Label>Email Address</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="email"
                  placeholder="search@company.com"
                  value={inviteSearch}
                  onChange={(e) => handleInviteEmailChange(e.target.value)}
                  className="pl-9"
                  data-testid="invite-email-input"
                />
                {inviteSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>

              {/* Search results dropdown */}
              {inviteSearchResults && inviteSearchResults.length > 0 && !foundUser && (
                <div className="border rounded-md divide-y max-h-40 overflow-y-auto">
                  {inviteSearchResults.map(u => (
                    <button key={u.id} className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition"
                      onClick={() => { setFoundUser(u); setInviteSearch(u.email); setInviteData(prev => ({ ...prev, email: u.email })); setInviteSearchResults(null); }}>
                      <p className="font-medium">{u.full_name}</p>
                      <p className="text-muted-foreground text-xs">{u.email}</p>
                    </button>
                  ))}
                </div>
              )}

              {/* User found card */}
              {foundUser && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="user-found-card">
                  <UserCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-emerald-800">{foundUser.full_name}</p>
                    <p className="text-xs text-emerald-600">{foundUser.email} · {foundUser.account_type || 'user'}</p>
                    {foundUser.last_login && <p className="text-xs text-emerald-500">Last login: {new Date(foundUser.last_login).toLocaleDateString()}</p>}
                  </div>
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                </div>
              )}

              {/* Not found — will create */}
              {inviteSearchResults !== null && inviteSearchResults.length === 0 && inviteSearch.includes('@') && !inviteSearching && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="user-will-create-card">
                  <UserPlus className="w-5 h-5 text-blue-600 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800">New user will be created</p>
                    <p className="text-xs text-blue-600">A new account will be created for <strong>{inviteSearch}</strong> and login credentials sent via email.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Role */}
            <div className="space-y-2">
              <Label>Assign Role</Label>
              <Select value={inviteData.role} onValueChange={(v) => setInviteData(prev => ({ ...prev, role: v }))}>
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="team_member">Team Member</SelectItem>
                  <SelectItem value="auditor">Auditor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting || !inviteData.email} data-testid="confirm-invite-btn">
              {inviting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding...</> : <><UserPlus className="w-4 h-4 mr-2" />Add to Tenant</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDetailPage;
